import React, { useState, useRef, useEffect, useMemo, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { FiCalendar, FiChevronLeft, FiChevronRight, FiX } from 'react-icons/fi';
import { getDateLocale } from '../../utils/dateFormat';
import './DateField.scss';

// ── Helpers ──────────────────────────────────────────────────
// Values are exchanged in the native <input type="date"> format: 'YYYY-MM-DD'.
function parseISO(value) {
  if (!value || typeof value !== 'string') return null;
  const [y, m, d] = value.split('-').map(Number);
  if (!y || !m || !d) return null;
  const dt = new Date(y, m - 1, d);
  return Number.isNaN(dt.getTime()) ? null : dt;
}
function toISO(dt) {
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const d = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}
function sameDay(a, b) {
  return !!a && !!b
    && a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate();
}
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

const POP_WIDTH = 300;
const POP_EST_HEIGHT = 340;

/**
 * Modern date picker with a calendar popover. Drop-in replacement for
 * <input type="date">, exchanging 'YYYY-MM-DD' strings via onChange(value).
 * The popover renders in a portal so it is never clipped by overflow:hidden
 * ancestors (e.g. section cards).
 */
function DateField({ value, onChange, placeholder, disabled, id, min, max, clearable = true, required }) {
  const { i18n, t } = useTranslation();
  // Prefer the org's region-aware locale (language + country) so month/day
  // order matches the rest of the app; fall back to the bare UI language.
  const lang = getDateLocale() || i18n.language || 'en';
  const wrapRef = useRef(null);
  const popRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const [view, setView] = useState('days'); // 'days' | 'months' | 'years'

  const selected = useMemo(() => parseISO(value), [value]);
  const minD = useMemo(() => parseISO(min), [min]);
  const maxD = useMemo(() => parseISO(max), [max]);
  const [viewDate, setViewDate] = useState(selected || new Date());

  // Localized formatters (month/weekday names follow the UI language).
  // The trigger shows a hard numeric date in the region's order (18.08.2026).
  const fmtLong = useMemo(() => new Intl.DateTimeFormat(lang, { day: '2-digit', month: '2-digit', year: 'numeric' }), [lang]);
  const fmtTitle = useMemo(() => new Intl.DateTimeFormat(lang, { month: 'long', year: 'numeric' }), [lang]);
  const weekdays = useMemo(() => {
    const fmt = new Intl.DateTimeFormat(lang, { weekday: 'short' });
    // 2023-01-02 is a Monday → week starts Monday.
    return Array.from({ length: 7 }, (_, i) => fmt.format(new Date(2023, 0, 2 + i)));
  }, [lang]);
  const monthNames = useMemo(() => {
    const fmt = new Intl.DateTimeFormat(lang, { month: 'short' });
    return Array.from({ length: 12 }, (_, i) => cap(fmt.format(new Date(2023, i, 1))));
  }, [lang]);
  const yearRangeStart = Math.floor(viewDate.getFullYear() / 12) * 12;

  // Compute a fixed-position placement anchored to the trigger, flipping above
  // when there isn't enough room below and clamping inside the viewport.
  const updatePosition = useCallback(() => {
    const el = wrapRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const gap = 6;
    const popH = popRef.current?.offsetHeight || POP_EST_HEIGHT;
    const spaceBelow = window.innerHeight - r.bottom;
    const up = spaceBelow < popH + gap && r.top > popH + gap;
    let left = Math.min(r.left, window.innerWidth - POP_WIDTH - 8);
    left = Math.max(8, left);
    const top = up ? Math.max(8, r.top - popH - gap) : r.bottom + gap;
    setPos({ left, top });
  }, []);

  // Re-centre on the selected month (and reset to the day grid) when opening.
  useEffect(() => { if (open) { setViewDate(selected || new Date()); setView('days'); } }, [open, selected]);

  // Position before paint, then keep it in sync while open.
  useLayoutEffect(() => { if (open) updatePosition(); }, [open, viewDate, view, updatePosition]);
  useEffect(() => {
    if (!open) return undefined;
    const handler = () => updatePosition();
    window.addEventListener('scroll', handler, true);
    window.addEventListener('resize', handler);
    return () => { window.removeEventListener('scroll', handler, true); window.removeEventListener('resize', handler); };
  }, [open, updatePosition]);

  // Close on outside click / Escape (accounting for the portaled popover).
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current?.contains(e.target)) return;
      if (popRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const grid = useMemo(() => {
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const first = new Date(year, month, 1);
    const startOffset = (first.getDay() + 6) % 7; // Monday-first
    const start = new Date(year, month, 1 - startOffset);
    return Array.from({ length: 42 }, (_, i) => new Date(start.getFullYear(), start.getMonth(), start.getDate() + i));
  }, [viewDate]);

  const today = new Date();
  const isDisabledDay = (d) => (minD && d < minD) || (maxD && d > maxD);

  const pick = (d) => {
    if (isDisabledDay(d)) return;
    onChange(toISO(d));
    setOpen(false);
  };

  return (
    <div className={`dfield${disabled ? ' dfield--disabled' : ''}`} ref={wrapRef}>
      <button
        type="button"
        id={id}
        className={`dfield__trigger${open ? ' is-open' : ''}${!selected ? ' is-placeholder' : ''}`}
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        data-required={required || undefined}
      >
        <FiCalendar className="dfield__icon" />
        <span className="dfield__value">
          {selected ? fmtLong.format(selected) : (placeholder || t('common.selectDate', { defaultValue: 'Select date' }))}
        </span>
        {clearable && selected && !disabled && (
          <span
            className="dfield__clear"
            role="button"
            tabIndex={-1}
            aria-label={t('common.clear', { defaultValue: 'Clear' })}
            onClick={(e) => { e.stopPropagation(); onChange(''); }}
          >
            <FiX />
          </span>
        )}
      </button>

      {open && createPortal(
        <div
          className="dfield__pop"
          role="dialog"
          ref={popRef}
          style={{ left: pos?.left ?? -9999, top: pos?.top ?? -9999 }}
        >
          <div className="dfield__head">
            <button type="button" className="dfield__nav" aria-label="Previous"
              onClick={() => {
                if (view === 'days') setViewDate((v) => new Date(v.getFullYear(), v.getMonth() - 1, 1));
                else if (view === 'months') setViewDate((v) => new Date(v.getFullYear() - 1, v.getMonth(), 1));
                else setViewDate((v) => new Date(v.getFullYear() - 12, v.getMonth(), 1));
              }}>
              <FiChevronLeft />
            </button>
            <button
              type="button"
              className="dfield__title"
              onClick={() => setView(view === 'days' ? 'months' : view === 'months' ? 'years' : 'months')}
            >
              {view === 'days' ? cap(fmtTitle.format(viewDate))
                : view === 'months' ? String(viewDate.getFullYear())
                : `${yearRangeStart} – ${yearRangeStart + 11}`}
            </button>
            <button type="button" className="dfield__nav" aria-label="Next"
              onClick={() => {
                if (view === 'days') setViewDate((v) => new Date(v.getFullYear(), v.getMonth() + 1, 1));
                else if (view === 'months') setViewDate((v) => new Date(v.getFullYear() + 1, v.getMonth(), 1));
                else setViewDate((v) => new Date(v.getFullYear() + 12, v.getMonth(), 1));
              }}>
              <FiChevronRight />
            </button>
          </div>

          {view === 'days' && (
            <>
              <div className="dfield__weekdays">
                {weekdays.map((w, i) => <span key={i} className="dfield__weekday">{w}</span>)}
              </div>
              <div className="dfield__grid">
                {grid.map((d, i) => {
                  const outside = d.getMonth() !== viewDate.getMonth();
                  return (
                    <button
                      key={i}
                      type="button"
                      className={`dfield__day${outside ? ' is-outside' : ''}${sameDay(d, selected) ? ' is-selected' : ''}${sameDay(d, today) ? ' is-today' : ''}`}
                      onClick={() => pick(d)}
                      disabled={isDisabledDay(d)}
                    >
                      {d.getDate()}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {view === 'months' && (
            <div className="dfield__grid dfield__grid--cells">
              {monthNames.map((m, i) => {
                const isSel = selected && selected.getMonth() === i && selected.getFullYear() === viewDate.getFullYear();
                const isCur = today.getMonth() === i && today.getFullYear() === viewDate.getFullYear();
                return (
                  <button
                    key={i}
                    type="button"
                    className={`dfield__cell${isSel ? ' is-selected' : ''}${isCur && !isSel ? ' is-today' : ''}`}
                    onClick={() => { setViewDate((v) => new Date(v.getFullYear(), i, 1)); setView('days'); }}
                  >
                    {m}
                  </button>
                );
              })}
            </div>
          )}

          {view === 'years' && (
            <div className="dfield__grid dfield__grid--cells">
              {Array.from({ length: 12 }, (_, i) => yearRangeStart + i).map((y) => {
                const isSel = selected && selected.getFullYear() === y;
                const isCur = today.getFullYear() === y;
                return (
                  <button
                    key={y}
                    type="button"
                    className={`dfield__cell${isSel ? ' is-selected' : ''}${isCur && !isSel ? ' is-today' : ''}`}
                    onClick={() => { setViewDate((v) => new Date(y, v.getMonth(), 1)); setView('months'); }}
                  >
                    {y}
                  </button>
                );
              })}
            </div>
          )}

          <div className="dfield__foot">
            <button
              type="button"
              className="dfield__foot-btn"
              onClick={() => { const n = new Date(); if (!isDisabledDay(n)) { onChange(toISO(n)); setOpen(false); } }}
            >
              {t('common.today', { defaultValue: 'Today' })}
            </button>
            {selected && (
              <button
                type="button"
                className="dfield__foot-btn dfield__foot-btn--muted"
                onClick={() => { onChange(''); setOpen(false); }}
              >
                {t('common.clear', { defaultValue: 'Clear' })}
              </button>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default DateField;
